import { jwtDecode } from "jwt-decode";

const TOKEN_STORAGE_KEY = "appTokens";

export interface PersistedSessionIdentity {
	tenantId?: string;
	userEmail?: string;
}

const readStoredTokens = (): string | undefined => {
	try {
		return globalThis.localStorage?.getItem(TOKEN_STORAGE_KEY) ?? undefined;
	} catch {
		return undefined;
	}
};

const readAccessToken = (): string | undefined => {
	const stored = readStoredTokens();
	if (!stored) return undefined;
	try {
		const tokens = JSON.parse(stored) as Partial<TokenSet>;
		return tokens.accessToken || undefined;
	} catch {
		return undefined;
	}
};

/**
 * Resolves the tenant/user identity of the session signed in right now, read directly from the
 * access token in localStorage. A redux-persist transform only ever sees the raw persisted blob
 * (never live app state), so deriving identity from a persisted slice instead of this can never
 * observe a session change until a later write has already happened, which lets a stale tab set
 * survive into a different tenant's or user's first load.
 */
const resolveSessionIdentity = (): PersistedSessionIdentity => {
	const accessToken = readAccessToken();
	if (!accessToken) return {};
	try {
		const decoded = jwtDecode<SFJWT>(accessToken);
		return { tenantId: decoded.wsTenantId, userEmail: decoded.email };
	} catch {
		return {};
	}
};

export default resolveSessionIdentity;
